import { useEffect, useRef, useState } from 'react';

/** Distancia en px para disparar la recarga. */
export const PULL_THRESHOLD = 70;

/** Regla pura: solo en el tope y superando el umbral. Testeable sin DOM. */
export function shouldTriggerRefresh(
  pullDistance: number,
  atTop: boolean,
  threshold: number = PULL_THRESHOLD,
): boolean {
  return atTop && pullDistance >= threshold;
}

type UsePullToRefreshOptions = {
  /** Recarga (p. ej. `() => query.refetch()`). Se invoca una sola vez por gesto. */
  onRefresh: () => Promise<unknown>;
  threshold?: number;
  disabled?: boolean;
};

/**
 * Pull-to-refresh nativo para listas: arrastra hacia abajo en el tope.
 *
 * Sin librerias: listeners touch sobre el contenedor. No pelea con la
 * bottom nav (solo gesto vertical descendente en el tope del scroll).
 * Sin conexion no dispara (el OfflineIndicator ya avisa).
 */
export function usePullToRefresh({ onRefresh, threshold = PULL_THRESHOLD, disabled = false }: UsePullToRefreshOptions) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const bindRef = useRef<HTMLDivElement | null>(null);
  const startY = useRef<number | null>(null);
  const latestRefresh = useRef(onRefresh);
  useEffect(() => {
    latestRefresh.current = onRefresh;
  });

  useEffect(() => {
    const el = bindRef.current;
    if (!el || disabled) return;
    let dist = 0;
    let active = true;

    const onTouchStart = (e: TouchEvent) => {
      if (!active) return;
      startY.current = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null || !active) return;
      const atTop = window.scrollY <= 0;
      const d = (e.touches[0]?.clientY ?? startY.current) - startY.current;
      if (d > 0 && atTop && navigator.onLine !== false) {
        dist = Math.min(d, threshold * 2);
        setPullDistance(dist);
        if (e.cancelable) e.preventDefault();
      }
    };
    const onTouchEnd = () => {
      const d = dist;
      startY.current = null;
      dist = 0;
      setPullDistance(0);
      if (!active) return;
      if (shouldTriggerRefresh(d, window.scrollY <= 0, threshold) && navigator.onLine !== false) {
        active = false;
        setRefreshing(true);
        void latestRefresh
          .current()
          .catch(() => undefined)
          .finally(() => {
            setRefreshing(false);
            active = true;
          });
      }
    };

    // touchcancel comparte el reset: un gesto interrumpido no deja el indicador colgado.
    const onTouchCancel = () => {
      startY.current = null;
      dist = 0;
      setPullDistance(0);
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchCancel);
    return () => {
      active = false;
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [threshold, disabled]);

  return { bindRef, pullDistance, refreshing, pulling: pullDistance > 0 && !refreshing };
}
